import mongoose from "mongoose";
import { UnitValue } from "./shared";

export interface MasterAgreementGeneral {
  description: string;
  descriptionLanguage: string;
  duration: UnitValue;
  postingDate: Date;
  validFrom: Date;
  validTo: Date;
}

export interface MasterAgreementExtensions {
  deploymentCountry: string;
  purchaseOrderSplitType: string;
  endDateCalculated: boolean;
  salNumber: string;
  bigDealId: string;
  salesMethod: string;
  opportunityId: string;
  podRequired: boolean;
}

export interface MasterAgreementOrganization {
  distributionChannel: string;
  division: string;
  salesOrganization: string;
}

export interface MasterAgreementPartner {
  partnerId: string;
  partnerType: string;
}

export interface MasterAgreementProductSolutionExtension {
  bundleId: string;
  localizationCodes: string[];
  solutionType: string;
}

export interface MasterAgreementSubProductOfferExtension {
  mainItem: boolean;
  itemType: string;
}

export interface MasterAgreementSubProductCharacteristic {
  characteristicsName: string;
  characteristicsValue: string;
}

export interface MasterAgreementSubProduct {
  productId: string;
  offerDescription: string;
  offerExtensions: MasterAgreementSubProductOfferExtension;
  obsolete: boolean;
  numberExt: number;
  characteristics: MasterAgreementSubProductCharacteristic[];
}

export interface MasterAgreementProduct {
  solutionExtensions: MasterAgreementProductSolutionExtension;
  dealId: string;
  customId: string; // DS-ID
  customText: string;
  duration: UnitValue;
  numberExt: number;
  productId: string;
  quantity: UnitValue;
  billingCycle: string;
  subProducts: MasterAgreementSubProduct[];
}

export interface MasterAgreement {
  endCustomerAccountId: string;
  resellerAccountId?: string;
  masterAgreementContractNumber: string;
  externalId: string;
  internalId: string;
  general: MasterAgreementGeneral;
  extensions: MasterAgreementExtensions;
  organization: MasterAgreementOrganization;
  partners: MasterAgreementPartner[];
  products: MasterAgreementProduct[];
}

interface NoID {
  _id: false;
}

export const MasterAgreementSchema = new mongoose.Schema<MasterAgreement>({
  endCustomerAccountId: { type: String, index: true },
  resellerAccountId: { type: String, index: true },
  masterAgreementContractNumber: { type: String, index: true },
  externalId: { type: String, index: true, unique: true },
  internalId: { type: String, index: true, unique: true },
  general: new mongoose.Schema<MasterAgreementGeneral & NoID>({
    _id: false,
    description: String,
    descriptionLanguage: String,
    duration: new mongoose.Schema<UnitValue & NoID>({
      _id: false,
      value: Number,
      unit: String,
    }),
    postingDate: Date,
    validFrom: Date,
    validTo: Date,
  }),
  extensions: new mongoose.Schema<MasterAgreementExtensions & NoID>({
    _id: false,
    deploymentCountry: String,
    purchaseOrderSplitType: String,
    endDateCalculated: { type: Boolean },
    salNumber: String,
    bigDealId: String,
    salesMethod: String,
    opportunityId: { type: String, index: true },
    podRequired: { type: Boolean },
  }),
  organization: new mongoose.Schema<MasterAgreementOrganization & NoID>({
    _id: false,
    distributionChannel: String,
    division: String,
    salesOrganization: String,
  }),
  partners: [new mongoose.Schema<MasterAgreementPartner & NoID>({
    _id: false,
    partnerId: String,
    partnerType: String,
  })],
  products: [new mongoose.Schema<MasterAgreementProduct & NoID>({
    _id: false,
    solutionExtensions: new mongoose.Schema<MasterAgreementProductSolutionExtension & NoID>({
      _id: false,
      bundleId: String,
      localizationCodes: [String],
      solutionType: String,
    }),
    dealId: String,
    customId: { type: String, index: true },
    customText: String,
    duration: new mongoose.Schema<UnitValue & NoID>({
      _id: false,
      value: Number,
      unit: String,
    }),
    numberExt: Number,
    productId: String,
    quantity: new mongoose.Schema<UnitValue & NoID>({
      _id: false,
      value: Number,
      unit: String,
    }),
    billingCycle: String,
    subProducts: [new mongoose.Schema<MasterAgreementSubProduct & NoID>({
      _id: false,
      productId: String,
      offerDescription: String,
      offerExtensions: new mongoose.Schema<MasterAgreementSubProductOfferExtension & NoID>({
        _id: false,
        mainItem: Boolean,
        itemType: String,
      }),
      obsolete: Boolean,
      numberExt: Number,
      characteristics: [new mongoose.Schema<MasterAgreementSubProductCharacteristic & NoID>({
        _id: false,
        characteristicsName: String,
        characteristicsValue: String,
      })],
    })],
  })],
});
